import React, { useState, useEffect } from 'react';
import { getSettings, saveSettings } from '../services/api';
import { THEMES, themeChoisi, themeEffectif, definirTheme } from '../theme';
import { useAuth } from '../App';

const TABS = [
  { id: 'general', label: 'Général' },
  { id: 'dashboard', label: 'Tableau de bord' },
  { id: 'sms', label: 'SMS & Alertes' },
  { id: 'pages', label: 'Pages' },
];

const DEFAULT = {
  general: { nom_societe: 'VALEA', contact_ca_nom: 'Conduite d\'activité VALEA', contact_ca_telephone: '' },
  dashboard: { widgets: { kpis: true, cri_counter: true, cr_pending: true, activite_recente: true, import_jour: true, techniciens: true } },
  sms: { enabled: true, heure_envoi: '12' },
  pages: {
    planning: { vue_defaut: 'week', secteur_defaut: 'tous' },
    planification: { secteur_defaut: 'tous' },
  },
};

function deepMerge(target, source) {
  const out = { ...target };
  for (const k of Object.keys(source || {})) {
    if (source[k] && typeof source[k] === 'object' && !Array.isArray(source[k])) {
      out[k] = deepMerge(target[k] || {}, source[k]);
    } else if (source[k] !== undefined) {
      out[k] = source[k];
    }
  }
  return out;
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <div
        onClick={onChange}
        style={{
          width: 42, height: 24, borderRadius: 12, flexShrink: 0,
          background: checked ? 'var(--blue2)' : 'var(--border)',
          position: 'relative', transition: 'background .2s', cursor: 'pointer',
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: checked ? 21 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
        }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        {description && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{description}</div>}
      </div>
    </label>
  );
}

function Field({ label, description, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</label>
      {children}
      {description && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>{description}</div>}
    </div>
  );
}

// Le theme ne passe pas par le serveur : c'est une preference d'affichage, elle
// tient dans le navigateur et n'a pas a etre la meme pour tout le monde sur un
// meme compte. Il s'applique donc sans attendre le bouton Sauvegarder.
function ChoixTheme() {
  const [choix, setChoix] = useState(themeChoisi);
  const effectif = themeEffectif(choix);
  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        {THEMES.map(t => (
          <button key={t.id}
            onClick={() => { definirTheme(t.id); setChoix(t.id); }}
            style={{
              flex: 1, fontSize: 13, fontWeight: 600, padding: '9px 0', borderRadius: 8, cursor: 'pointer',
              background: choix === t.id ? 'rgba(37,99,235,0.14)' : 'var(--bg3)',
              color: choix === t.id ? 'var(--blue2)' : 'var(--text2)',
              border: `1px solid ${choix === t.id ? 'var(--blue2)' : 'var(--border)'}`,
            }}>
            {t.label}
          </button>
        ))}
      </div>
      {choix === 'systeme' && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
          Votre systeme demande le theme {effectif}.
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const [tab, setTab] = useState('general');
  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getSettings()
      .then(data => setForm(deepMerge(DEFAULT, data)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setG = (key, val) => setForm(f => ({ ...f, general: { ...f.general, [key]: val } }));
  const setWidget = (key, val) => setForm(f => ({ ...f, dashboard: { ...f.dashboard, widgets: { ...f.dashboard.widgets, [key]: val } } }));
  const setSms = (key, val) => setForm(f => ({ ...f, sms: { ...f.sms, [key]: val } }));
  const setPage = (page, key, val) => setForm(f => ({ ...f, pages: { ...f.pages, [page]: { ...f.pages[page], [key]: val } } }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveSettings(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // La conduite d'activité n'a ici que le thème. Le reste — la société, les
  // widgets du tableau de bord, les alertes SMS, les pages autorisées — engage
  // tout le monde et reste à l'administration. Mais choisir entre le clair et
  // le sombre est une préférence d'affichage, gardée par le navigateur : elle
  // n'a jamais eu à se demander à quelqu'un.
  if (!isAdmin) return (
    <>
      <div className="topbar"><div className="topbar-title">Paramètres</div></div>
      <div className="page">
        <div style={{ maxWidth: 640 }}>
          <div className="card">
            <Field label="Thème de l’interface"
              description="S’applique tout de suite, et seulement sur ce navigateur — rien à sauvegarder.">
              <ChoixTheme />
            </Field>
          </div>
        </div>
      </div>
    </>
  );

  if (loading) return (
    <>
      <div className="topbar"><div className="topbar-title">Paramètres</div></div>
      <div className="page"><div style={{ color: 'var(--text3)', padding: 40, textAlign: 'center' }}>Chargement…</div></div>
    </>
  );

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Paramètres</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {saved && <span style={{ fontSize: 13, color: 'var(--green2)', fontWeight: 600 }}>✓ Sauvegardé</span>}
          {error && <span style={{ fontSize: 13, color: '#ff6b6b' }}>{error}</span>}
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      <div className="page">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '8px 18px', fontSize: 13, fontWeight: 600,
                background: 'none', border: 'none', cursor: 'pointer',
                color: tab === t.id ? 'var(--blue2)' : 'var(--text3)',
                borderBottom: tab === t.id ? '2px solid var(--blue2)' : '2px solid transparent',
                marginBottom: -2, transition: 'color .15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ maxWidth: 640 }}>

          {/* ── GÉNÉRAL ─────────────────────────────────────────── */}
          {tab === 'general' && (
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 20 }}>Informations générales</div>
              <Field label="Nom de la société">
                <input className="form-input" value={form.general.nom_societe}
                  onChange={e => setG('nom_societe', e.target.value)} />
              </Field>
              <Field label="Nom contact conduite d'activité"
                description="Affiché dans les SMS envoyés aux techniciens">
                <input className="form-input" value={form.general.contact_ca_nom}
                  onChange={e => setG('contact_ca_nom', e.target.value)} />
              </Field>
              <Field label="Téléphone contact conduite d'activité"
                description="Numéro de rappel mentionné dans les alertes SMS">
                <input className="form-input" value={form.general.contact_ca_telephone}
                  onChange={e => setG('contact_ca_telephone', e.target.value)}
                  placeholder="Ex: 01 23 45 67 89" />
              </Field>

              <Field label="Thème de l’interface"
                description="S’applique tout de suite, et seulement sur ce navigateur — inutile de sauvegarder.">
                <ChoixTheme />
              </Field>
            </div>
          )}

          {/* ── TABLEAU DE BORD ──────────────────────────────────── */}
          {tab === 'dashboard' && (
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Widgets affichés</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
                Choisissez les informations à afficher sur le tableau de bord
              </div>
              <Toggle
                checked={form.dashboard.widgets.kpis}
                onChange={() => setWidget('kpis', !form.dashboard.widgets.kpis)}
                label="Compteurs KPI"
                description="Techniciens actifs, CR en attente, CLEARED ce mois, interventions"
              />
              <Toggle
                checked={form.dashboard.widgets.cri_counter}
                onChange={() => setWidget('cri_counter', !form.dashboard.widgets.cri_counter)}
                label="Compteur CRI / Alertes"
                description="Nombre d'interventions CRI non clôturé, RDVNH, CR non conforme"
              />
              <Toggle
                checked={form.dashboard.widgets.cr_pending}
                onChange={() => setWidget('cr_pending', !form.dashboard.widgets.cr_pending)}
                label="CR en attente de validation"
                description="Liste des comptes-rendus à traiter"
              />
              <Toggle
                checked={form.dashboard.widgets.activite_recente}
                onChange={() => setWidget('activite_recente', !form.dashboard.widgets.activite_recente)}
                label="Activité récente"
                description="Dernières actions effectuées dans le panel"
              />
              <Toggle
                checked={form.dashboard.widgets.import_jour}
                onChange={() => setWidget('import_jour', !form.dashboard.widgets.import_jour)}
                label="Import commandes du jour"
                description="Statistiques des imports effectués aujourd'hui"
              />
              <Toggle
                checked={form.dashboard.widgets.techniciens}
                onChange={() => setWidget('techniciens', !form.dashboard.widgets.techniciens)}
                label="Liste techniciens"
                description="Techniciens enregistrés dans l'application"
              />
            </div>
          )}

          {/* ── SMS & ALERTES ────────────────────────────────────── */}
          {tab === 'sms' && (
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 20 }}>Configuration des alertes WhatsApp</div>
              <Toggle
                checked={form.sms.enabled}
                onChange={() => setSms('enabled', !form.sms.enabled)}
                label="Alertes SMS activées"
                description="Envoyer automatiquement les alertes CRI / RDVNH / CR non conforme"
              />
              <div style={{ height: 20 }} />
              <Field label="Heure d'envoi quotidien"
                description="Les alertes seront envoyées chaque jour à cette heure (heure de Paris)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="number" min="0" max="23" className="form-input"
                    style={{ width: 80 }}
                    value={form.sms.heure_envoi}
                    onChange={e => setSms('heure_envoi', e.target.value)}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text2)' }}>h00</span>
                </div>
              </Field>
              <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px', marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--text2)' }}>Rappel — Types d'alertes envoyées :</div>
                <ul style={{ fontSize: 12, color: 'var(--text3)', margin: 0, paddingLeft: 16, lineHeight: '1.8' }}>
                  <li><strong style={{ color: 'var(--text)' }}>CRI non clôturé</strong> — SMS J+1, J+2, J+3 (1 SMS par jour de retard, max 3)</li>
                  <li><strong style={{ color: 'var(--text)' }}>RDVNH</strong> — 1 seul SMS lors de la première détection</li>
                  <li><strong style={{ color: 'var(--text)' }}>CR non conforme</strong> — 1 SMS par jour tant que l'étape est présente</li>
                </ul>
              </div>
            </div>
          )}

          {/* ── PAGES ───────────────────────────────────────────── */}
          {tab === 'pages' && (
            <>
              <div className="card" style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 20 }}>Page Planning</div>
                <Field label="Vue par défaut">
                  <select className="form-input" value={form.pages.planning.vue_defaut}
                    onChange={e => setPage('planning', 'vue_defaut', e.target.value)}>
                    <option value="week">Semaine</option>
                    <option value="day">Jour</option>
                    <option value="month">Mois</option>
                  </select>
                </Field>
                <Field label="Secteur par défaut">
                  <select className="form-input" value={form.pages.planning.secteur_defaut}
                    onChange={e => setPage('planning', 'secteur_defaut', e.target.value)}>
                    <option value="tous">Tous les secteurs</option>
                    <option value="A">Secteur A</option>
                    <option value="B">Secteur B</option>
                  </select>
                </Field>
              </div>

              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 20 }}>Page Planification RDV</div>
                <Field label="Secteur par défaut">
                  <select className="form-input" value={form.pages.planification.secteur_defaut}
                    onChange={e => setPage('planification', 'secteur_defaut', e.target.value)}>
                    <option value="tous">Tous les secteurs</option>
                    <option value="A">Secteur A</option>
                    <option value="B">Secteur B</option>
                  </select>
                </Field>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}
